import { MAX_IMAGE_SIZE, VALID_IMAGE_TYPE } from '@/constants/common'
import { ImageAsset } from '@/types/common'

type SupportedImageMimeType = (typeof VALID_IMAGE_TYPE)[number]

type ImageUploadMetadata = {
  extension: 'jpg' | 'png'
  mimeType: SupportedImageMimeType
}

type PickedImage = Pick<ImageAsset, 'uri' | 'fileName' | 'mimeType'>

export type PreparedImageUpload = { asset: ImageAsset; ok: true } | { ok: false; reason: 'size' | 'type' }

const metadataByMimeType: Record<SupportedImageMimeType, ImageUploadMetadata> = {
  'image/jpeg': { extension: 'jpg', mimeType: 'image/jpeg' },
  'image/png': { extension: 'png', mimeType: 'image/png' }
}

const metadataByExtension: Record<string, ImageUploadMetadata> = {
  jpeg: metadataByMimeType['image/jpeg'],
  jpg: metadataByMimeType['image/jpeg'],
  png: metadataByMimeType['image/png']
}

function normalizeMimeType(value?: string | null) {
  if (!value) return null

  try {
    const mimeType = decodeURIComponent(value).split(';', 1)[0].trim().toLowerCase()
    return mimeType === 'image/jpg' ? 'image/jpeg' : mimeType
  } catch {
    return value.split(';', 1)[0].trim().toLowerCase()
  }
}

function getMetadataFromMimeType(value?: string | null) {
  const mimeType = normalizeMimeType(value)
  if (!mimeType || !VALID_IMAGE_TYPE.includes(mimeType as SupportedImageMimeType)) return null
  return metadataByMimeType[mimeType as SupportedImageMimeType]
}

function getMetadataFromFileName(fileName?: string | null) {
  if (!fileName) return null
  const extension = fileName.split('.').at(-1)?.toLowerCase()
  return extension ? metadataByExtension[extension] || null : null
}

function isGenericMimeType(mimeType: string | null) {
  return !mimeType || mimeType === 'application/octet-stream'
}

/**
 * Android providers do not always expose a MIME type through fetch(uri). Prefer
 * a non-generic Blob MIME type, then picker metadata, and finally the filename.
 * Filename fallback is only safe when all supplied MIME types are missing or
 * generic. Unsupported explicit MIME types remain rejected.
 */
export function getSupportedImageUploadMetadata({
  fileName,
  mimeType,
  blobType
}: {
  blobType?: string | null
  fileName?: string | null
  mimeType?: string | null
}): ImageUploadMetadata | null {
  const normalizedPickerMimeType = normalizeMimeType(mimeType)
  const normalizedBlobType = normalizeMimeType(blobType)
  const pickerMetadata = getMetadataFromMimeType(normalizedPickerMimeType)
  const blobMetadata = getMetadataFromMimeType(normalizedBlobType)

  if (!isGenericMimeType(normalizedPickerMimeType) && !pickerMetadata) return null
  if (!isGenericMimeType(normalizedBlobType) && !blobMetadata) return null

  if (blobMetadata) return blobMetadata
  if (pickerMetadata) return pickerMetadata
  return getMetadataFromFileName(fileName)
}

export async function preparePickedImageUpload(asset: PickedImage): Promise<PreparedImageUpload> {
  const response = await fetch(asset.uri)
  const blob = await response.blob()
  const metadata = getSupportedImageUploadMetadata({
    fileName: asset.fileName,
    mimeType: asset.mimeType,
    blobType: blob.type
  })

  if (!metadata) return { ok: false, reason: 'type' }
  if (blob.size > MAX_IMAGE_SIZE) return { ok: false, reason: 'size' }

  return {
    ok: true,
    asset: {
      uri: asset.uri,
      fileName: `upload.${metadata.extension}`,
      mimeType: metadata.mimeType
    }
  }
}
